import useAxesAlignmentStore from '../../shared/stores/useAxesAlignmentStore';
import useElongationStore from '../../shared/stores/useElongationStore';
import useTrimlineStore from '../../shared/stores/useTrimlineStore';
import useRectificationStore from '../../shared/stores/useRectificationStore';
import useFlareStore from '../../shared/stores/useFlareStore';
import useFlaresSmootheningStore from '../../shared/stores/useFlaresSmootheningStore';
import useCarveSmootheningStore from '../../shared/stores/useCarveSmootheningStore';
import useMLReductionSmootheningStore from '../../shared/stores/useMLReductionSmootheningStore';
import useGlobalSmoothStore from '../../shared/stores/useGlobalSmoothStore';
import useLogoPlacementStore from '../../shared/stores/useLogoPlacementStore';
import useThicknessStore from '../../shared/stores/useThicknessStore';
import useOutwardThicknessStore from '../../shared/stores/useOutwardThicknessStore';
import useMLReductionStore from '../../shared/stores/useMLReductionStore';
import useLockingSystemStore from '../../shared/stores/useLockingSystemStore';
import useValveStore from '../../shared/stores/useValveStore';
import useFinalizationDataStore from '../../shared/stores/useFinalizationDataStore';
import useEditorStore from '../../shared/stores/useEditorStore';

function setToArray(set) {
  return set instanceof Set ? Array.from(set) : [];
}

function arrayToSet(arr) {
  return new Set(Array.isArray(arr) ? arr : []);
}

/**
 * The brush-smoothening stores (carve relief, ML reduction) share one shape, so
 * they share one capture/restore pair. Zone influences are Float32Arrays, which
 * do not survive the structured clone into the scene cache as-is.
 */
function captureSmootheningSlice(state) {
  return {
    smoothning: {
      enabled: state.smoothning?.enabled,
      zones: (state.smoothning?.zones ?? []).map((zone) => ({
        ...zone,
        influences: zone.influences ? Array.from(zone.influences) : zone.influences,
      })),
      brushRadius: state.smoothning?.brushRadius,
      previewVisible: state.smoothning?.previewVisible,
    },
    isSmootheningApplied: state.isSmootheningApplied,
  };
}

function restoreSmootheningSlice(store, data) {
  if (!data) return;
  store.setState({
    smoothning: {
      ...store.getState().smoothning,
      enabled: data.smoothning?.enabled,
      zones: (data.smoothning?.zones ?? []).map((zone) => ({
        ...zone,
        influences: zone.influences ? Float32Array.from(zone.influences) : zone.influences,
      })),
      brushRadius: data.smoothning?.brushRadius,
      previewVisible: data.smoothning?.previewVisible,
      previewMesh: null,
    },
    isSmootheningApplied: !!data.isSmootheningApplied,
    isSmootheningAppliedFromDraft: false,
    currentSmootheningCommand: null,
  });
}

/**
 * Serializable session slice captured when leaving a workflow step.
 * @typedef {Object} SessionSnapshot
 */

/** @returns {SessionSnapshot} */
export function captureSessionState() {
  const editor = useEditorStore.getState();
  const elongation = useElongationStore.getState();
  const trimline = useTrimlineStore.getState();
  const rectification = useRectificationStore.getState();
  const flare = useFlareStore.getState();
  const flaresSmooth = useFlaresSmootheningStore.getState();
  const thickness = useThicknessStore.getState();
  const outward = useOutwardThicknessStore.getState();
  const ml = useMLReductionStore.getState();
  const axes = useAxesAlignmentStore.getState();
  const locking = useLockingSystemStore.getState();
  const valve = useValveStore.getState();
  const carveSmooth = useCarveSmootheningStore.getState();
  const mlSmooth = useMLReductionSmootheningStore.getState();
  const globalSmooth = useGlobalSmoothStore.getState();
  const logo = useLogoPlacementStore.getState();
  const finalization = useFinalizationDataStore.getState();

  return {
    editor: {
      activeTool: editor.activeTool,
      showUnmodifiedMesh: editor.showUnmodifiedMesh,
      modelManualScale: editor.modelManualScale,
      modelScaleUnit: editor.modelScaleUnit,
      preparingScanSocketEulerDeg: editor.preparingScanSocketEulerDeg,
      alignmentSocketPose: editor.alignmentSocketPose,
      alignmentSocketRedRingXDeg: editor.alignmentSocketRedRingXDeg,
      alignmentSocketAbductionZDeg: editor.alignmentSocketAbductionZDeg,
      alignmentFootToePitchDeg: editor.alignmentFootToePitchDeg,
      logoPlacementBoundary: editor.logoPlacementBoundary,
      toolSettings: editor.toolSettings,
    },
    elongation: {
      enabled: elongation.enabled,
      value: elongation.value,
      unit: elongation.unit,
      isElongationApplied: elongation.isElongationApplied,
    },
    trimline: {
      trimline: {
        enabled: trimline.trimline?.enabled,
        points: trimline.trimline?.points ?? [],
        bezierHandles: trimline.trimline?.bezierHandles ?? [],
        selectedPointIndex: trimline.trimline?.selectedPointIndex ?? null,
        showBezierHandles: trimline.trimline?.showBezierHandles ?? false,
        previewVisible: trimline.trimline?.previewVisible ?? false,
      },
      applied: trimline.applied,
      isTrimlineApplied: trimline.isTrimlineApplied,
    },
    rectification: {
      measureReduction: {
        measurements: rectification.measureReduction?.measurements ?? [],
      },
      globalReduction: {
        value: rectification.globalReduction?.value,
        valueType: rectification.globalReduction?.valueType,
        unit: rectification.globalReduction?.unit,
        // What the applied chain adds up to; without it a re-apply after a
        // reload would report only the newest pass to the finalization record.
        appliedTotal: rectification.globalReduction?.appliedTotal ?? null,
      },
      isRectificationApplied: rectification.isRectificationApplied,
      isGlobalReductionApplied: rectification.isGlobalReductionApplied,
      isScoopRechargeApplied: rectification.isScoopRechargeApplied,
      isSmootheningApplied: rectification.isSmootheningApplied,
    },
    flares: {
      flareValues: flare.flareValues,
      segmentHeightValues: flare.segmentHeightValues,
      segmentDeflectValues: flare.segmentDeflectValues,
      segmentFalloffHeightValues: flare.segmentFalloffHeightValues,
      segmentFalloffDeflectValues: flare.segmentFalloffDeflectValues,
      deflectBlendRatio: flare.deflectBlendRatio,
      segmentAssignmentMode: flare.segmentAssignmentMode,
      selectedSegmentIndex: flare.selectedSegmentIndex,
      isFlareApplied: flare.isFlareApplied,
      smoothning: {
        enabled: flaresSmooth.smoothning?.enabled,
        zones: (flaresSmooth.smoothning?.zones ?? []).map((zone) => ({
          ...zone,
          influences: zone.influences ? Array.from(zone.influences) : zone.influences,
        })),
        brushRadius: flaresSmooth.smoothning?.brushRadius,
        previewVisible: flaresSmooth.smoothning?.previewVisible,
      },
      isSmootheningApplied: flaresSmooth.isSmootheningApplied,
    },
    thickness: {
      legacy: {
        thickness: thickness.thickness,
        isThicknessApplied: thickness.isThicknessApplied,
      },
      outward: {
        wallMm: outward.wallMm,
        offsetMm: outward.offsetMm,
        previewVisible: outward.previewVisible,
        manifoldSegments: outward.manifoldSegments,
        isApplied: outward.isApplied,
      },
    },
    mlReduction: {
      regionA: setToArray(ml.regionA),
      regionB: setToArray(ml.regionB),
      activeRegion: ml.activeRegion,
      brush: ml.brush,
      reductionMm: ml.reductionMm,
      distanceMm: ml.distanceMm,
      isDistanceReady: ml.isDistanceReady,
      canUndoReduction: ml.canUndoReduction,
      mlReductionApplyConsumed: ml.mlReductionApplyConsumed,
      reductionPositionBackup: ml.reductionPositionBackup
        ? Array.from(ml.reductionPositionBackup)
        : null,
    },
    axesAlignment: {
      isAligned: axes.isAligned,
      landmarkPositions: axes.landmarkPositions,
      selectionsCompleted: Object.fromEntries(
        Object.entries(axes.selections ?? {}).map(([k, v]) => [k, !!v?.completed]),
      ),
    },
    locking: {
      lockingSystem: {
        selectedSystem: locking.lockingSystem?.selectedSystem,
        diameter: locking.lockingSystem?.diameter,
        depth: locking.lockingSystem?.depth,
        customizationEnabled: locking.lockingSystem?.customizationEnabled,
      },
      bulldog: {
        phase: locking.bulldog?.phase,
        wallAngleDeg: locking.bulldog?.wallAngleDeg,
        curvature: locking.bulldog?.curvature,
        curvePosition: locking.bulldog?.curvePosition,
        heightAdjustCm: locking.bulldog?.heightAdjustCm,
        isPreviewing: locking.bulldog?.isPreviewing,
      },
      fourHoleAttachment: {
        phase: locking.fourHoleAttachment?.phase,
        wallAngleDeg: locking.fourHoleAttachment?.wallAngleDeg,
        curvature: locking.fourHoleAttachment?.curvature,
        curvePosition: locking.fourHoleAttachment?.curvePosition,
        frustumThicknessCm: locking.fourHoleAttachment?.frustumThicknessCm,
        isPreviewing: locking.fourHoleAttachment?.isPreviewing,
      },
      positioningPanelTab: locking.positioningPanelTab,
      attachmentTransformMode: locking.attachmentTransformMode,
    },
    valve: {
      selectedValveId: valve.selectedValveId,
      valvePlaced: valve.valvePlaced,
      valveApplied: valve.valveApplied,
      valve: valve.valve,
    },
    // Rectification's Carve Relief smoothing and ML Reduction's smoothing live in
    // their own stores (not the `rectification` / `mlReduction` slices above), so
    // they need their own entries or their cards survive a revert still "applied".
    carveSmoothening: captureSmootheningSlice(carveSmooth),
    mlSmoothening: captureSmootheningSlice(mlSmooth),
    globalSmooth: {
      intensity: globalSmooth.intensity,
      lockVolume: globalSmooth.lockVolume,
      // One entry per step that offers global smoothing (Prepare Scan, Flares).
      appliedByTool: { ...globalSmooth.appliedByTool },
    },
    logo: {
      isPlaced: logo.isPlaced,
      logoApplied: logo.logoApplied,
      // The custom texture is an object URL owned by this tab; only its name is
      // meaningful once serialized, and the store re-derives the rest.
      customLogoFileName: logo.customLogoFileName,
    },
    finalization: {
      appliedTools: finalization.appliedTools,
    },
  };
}

/** @param {SessionSnapshot} snapshot */
export function restoreSessionState(snapshot) {
  if (!snapshot) return;

  if (snapshot.editor) {
    const e = snapshot.editor;
    // Compare mode can only come back on if the restored scene actually carries the original model.
    const hasCompareBaseline =
      !!useEditorStore.getState().sceneRefs?.unmodifiedMesh?.geometry?.attributes?.position?.count;
    useEditorStore.setState({
      activeTool: e.activeTool ?? 'none',
      showUnmodifiedMesh: hasCompareBaseline && !!e.showUnmodifiedMesh,
      modelManualScale: e.modelManualScale ?? 1,
      modelScaleUnit: e.modelScaleUnit ?? 'mm',
      preparingScanSocketEulerDeg: e.preparingScanSocketEulerDeg ?? { x: 0, y: 0, z: 0 },
      alignmentSocketPose: e.alignmentSocketPose ?? null,
      alignmentSocketRedRingXDeg: e.alignmentSocketRedRingXDeg ?? 0,
      alignmentSocketAbductionZDeg: e.alignmentSocketAbductionZDeg ?? 0,
      alignmentFootToePitchDeg: e.alignmentFootToePitchDeg ?? 0,
      logoPlacementBoundary: e.logoPlacementBoundary ?? null,
      toolSettings: e.toolSettings ?? useEditorStore.getState().toolSettings,
      history: [],
      historyIndex: -1,
    });
  }

  if (snapshot.elongation) {
    const d = snapshot.elongation;
    useElongationStore.setState({
      enabled: d.enabled ?? true,
      value: d.value ?? 0,
      unit: d.unit ?? 'mm',
      isElongationApplied: !!d.isElongationApplied,
      isAppliedFromDraft: false,
      currentElongationCommand: null,
      previewMesh: null,
    });
  }

  if (snapshot.trimline) {
    const d = snapshot.trimline;
    useTrimlineStore.setState({
      trimline: {
        ...useTrimlineStore.getState().trimline,
        ...d.trimline,
      },
      applied: !!d.applied,
      isTrimlineApplied: !!d.isTrimlineApplied,
      isAppliedFromDraft: false,
      currentTrimlineCommand: null,
      previewMesh: null,
      upperMesh: null,
    });
  }

  if (snapshot.rectification) {
    const d = snapshot.rectification;
    useRectificationStore.setState({
      isRectificationApplied: !!d.isRectificationApplied,
      isGlobalReductionApplied: !!d.isGlobalReductionApplied,
      isScoopRechargeApplied: !!d.isScoopRechargeApplied,
      isSmootheningApplied: !!d.isSmootheningApplied,
      isRectificationAppliedFromDraft: false,
      isGlobalReductionAppliedFromDraft: false,
      isScoopRechargeAppliedFromDraft: false,
      isSmootheningAppliedFromDraft: false,
      currentRectificationCommand: null,
      currentGlobalReductionCommand: null,
      currentScoopRechargeCommand: null,
      currentSmootheningCommand: null,
      measureReduction: {
        ...useRectificationStore.getState().measureReduction,
        measurements: d.measureReduction?.measurements ?? [],
      },
      globalReduction: {
        ...useRectificationStore.getState().globalReduction,
        value: d.globalReduction?.value ?? 0,
        valueType: d.globalReduction?.valueType ?? '%',
        unit: d.globalReduction?.unit ?? 'mm',
        appliedTotal: d.globalReduction?.appliedTotal ?? null,
      },
    });
  }

  if (snapshot.flares) {
    const d = snapshot.flares;
    const restoredZones = (d.smoothning?.zones ?? []).map((zone) => ({
      ...zone,
      influences: zone.influences ? Float32Array.from(zone.influences) : zone.influences,
    }));
    useFlareStore.setState({
      flareValues: d.flareValues ?? useFlareStore.getState().flareValues,
      segmentHeightValues: d.segmentHeightValues ?? useFlareStore.getState().segmentHeightValues,
      segmentDeflectValues: d.segmentDeflectValues ?? useFlareStore.getState().segmentDeflectValues,
      segmentFalloffHeightValues:
        d.segmentFalloffHeightValues ?? useFlareStore.getState().segmentFalloffHeightValues,
      segmentFalloffDeflectValues:
        d.segmentFalloffDeflectValues ?? useFlareStore.getState().segmentFalloffDeflectValues,
      deflectBlendRatio: d.deflectBlendRatio ?? useFlareStore.getState().deflectBlendRatio,
      segmentAssignmentMode:
        d.segmentAssignmentMode ?? useFlareStore.getState().segmentAssignmentMode,
      selectedSegmentIndex: d.selectedSegmentIndex ?? useFlareStore.getState().selectedSegmentIndex,
      isFlareApplied: !!d.isFlareApplied,
      isFlareAppliedFromDraft: false,
      currentFlareCommand: null,
      previewMesh: null,
      baseGeometry: null,
    });
    useFlaresSmootheningStore.setState({
      smoothning: {
        ...useFlaresSmootheningStore.getState().smoothning,
        enabled: d.smoothning?.enabled,
        zones: restoredZones,
        brushRadius: d.smoothning?.brushRadius,
        previewVisible: d.smoothning?.previewVisible,
        previewMesh: null,
      },
      isSmootheningApplied: !!d.isSmootheningApplied,
      isSmootheningAppliedFromDraft: false,
      currentSmootheningCommand: null,
    });
  }

  if (snapshot.thickness) {
    const legacy = snapshot.thickness.legacy;
    const outward = snapshot.thickness.outward;
    if (legacy) {
      useThicknessStore.setState({
        thickness: { ...useThicknessStore.getState().thickness, ...legacy.thickness },
        isThicknessApplied: !!legacy.isThicknessApplied,
        isThicknessAppliedFromDraft: false,
        currentThicknessCommand: null,
        previewMesh: null,
      });
    }
    if (outward) {
      useOutwardThicknessStore.setState({
        wallMm: outward.wallMm ?? useOutwardThicknessStore.getState().wallMm,
        offsetMm: outward.offsetMm ?? useOutwardThicknessStore.getState().offsetMm,
        previewVisible: outward.previewVisible ?? true,
        manifoldSegments: outward.manifoldSegments ?? useOutwardThicknessStore.getState().manifoldSegments,
        isApplied: !!outward.isApplied,
        currentCommand: null,
        previewMesh: null,
      });
    }
  }

  if (snapshot.mlReduction) {
    const d = snapshot.mlReduction;
    useMLReductionStore.setState({
      regionA: arrayToSet(d.regionA),
      regionB: arrayToSet(d.regionB),
      activeRegion: d.activeRegion ?? 'A',
      brush: d.brush ?? useMLReductionStore.getState().brush,
      reductionMm: d.reductionMm ?? 0,
      distanceMm: d.distanceMm ?? null,
      isDistanceReady: !!d.isDistanceReady,
      canUndoReduction: !!d.canUndoReduction,
      mlReductionApplyConsumed: !!d.mlReductionApplyConsumed,
      reductionPositionBackup: d.reductionPositionBackup
        ? Float32Array.from(d.reductionPositionBackup)
        : null,
    });
  }

  if (snapshot.axesAlignment) {
    const d = snapshot.axesAlignment;
    const completed = d.selectionsCompleted ?? {};
    useAxesAlignmentStore.setState({
      isAligned: !!d.isAligned,
      landmarkPositions: d.landmarkPositions ?? null,
      selections: {
        subPatellar: { sphere: null, completed: !!completed.subPatellar },
        midPatellar: { sphere: null, completed: !!completed.midPatellar },
        frontEnd: { sphere: null, completed: !!completed.frontEnd },
        internalCondyle: { sphere: null, completed: !!completed.internalCondyle },
        externalCondyle: { sphere: null, completed: !!completed.externalCondyle },
      },
    });
    if (d.landmarkPositions) {
      useAxesAlignmentStore.getState().persistLandmarkPositionsToBkStorage?.();
    }
  }

  if (snapshot.locking) {
    const d = snapshot.locking;
    useLockingSystemStore.setState({
      lockingSystem: {
        ...useLockingSystemStore.getState().lockingSystem,
        ...d.lockingSystem,
      },
      bulldog: {
        ...useLockingSystemStore.getState().bulldog,
        ...d.bulldog,
        adapterMesh: null,
        formMesh: null,
      },
      fourHoleAttachment: {
        ...useLockingSystemStore.getState().fourHoleAttachment,
        ...d.fourHoleAttachment,
        adapterMesh: null,
        formMesh: null,
      },
      positioningPanelTab: d.positioningPanelTab ?? 'socket',
      attachmentTransformMode: d.attachmentTransformMode ?? 'translate',
      pendingAttachmentCommand: null,
      currentAttachmentCommand: null,
    });
  }

  if (snapshot.valve) {
    const d = snapshot.valve;
    useValveStore.setState({
      selectedValveId: d.selectedValveId ?? null,
      valvePlaced: !!d.valvePlaced,
      valveApplied: !!d.valveApplied,
      valve: d.valve ?? useValveStore.getState().valve,
      valveStatus: d.selectedValveId ? 'idle' : 'idle',
      valveReady: !!d.valveApplied,
      valveApplyStatus: d.valveApplied ? 'applied' : 'idle',
      valveApplyRequestId: 0,
      valveUndoRequestId: 0,
      valveError: null,
    });
  }

  restoreSmootheningSlice(useCarveSmootheningStore, snapshot.carveSmoothening);
  restoreSmootheningSlice(useMLReductionSmootheningStore, snapshot.mlSmoothening);

  if (snapshot.globalSmooth) {
    const d = snapshot.globalSmooth;
    useGlobalSmoothStore.setState({
      intensity: d.intensity ?? useGlobalSmoothStore.getState().intensity,
      lockVolume: d.lockVolume ?? true,
      appliedByTool: { ...(d.appliedByTool ?? {}) },
      appliedFromDraftByTool: {},
      commandByTool: {},
      pendingCommand: null,
      isSmoothing: false,
    });
  }

  if (snapshot.logo) {
    const d = snapshot.logo;
    useLogoPlacementStore.setState({
      isPlaced: !!d.isPlaced,
      logoApplied: !!d.logoApplied,
      logoApplyStatus: d.logoApplied ? 'applied' : 'idle',
      logoApplyInProgress: false,
      logoError: null,
    });
  }

  if (snapshot.finalization?.appliedTools) {
    useFinalizationDataStore.setState({
      appliedTools: snapshot.finalization.appliedTools,
    });
  }
}
